import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import api from '../../../api/axios';
import { getCountryCodes } from '../../../utils/common';
import { useLoading } from '../../../components/LoadingProvider';
import { showSuccess, showError } from '../../../utils/toast';
import { getPublicImgUrl } from '../../../utils/common';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

function ClinicTeamMembers() {
    const navigate = useNavigate();
    const errorDivRef = useRef(null);
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['clinic'] });
    const [membersList, setMembersList] = useState([]);
    const { setIsLoading } = useLoading();
    const MySwal = withReactContent(Swal);
    const countryCodes = getCountryCodes();
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [errors, setErrors] = useState({
        user_name: '',
        user_phone_code: '',
        user_phone_number: '',
        user_email: '',
        password: '',
        password_confirmation: ''
    });

    const [formData, setFormData] = useState({
        user_name: '',
        user_phone_code: '+91',
        user_phone_number: '',
        user_email: '',
        password: '',
        password_confirmation: ''
    });

    const resetForm = () => {
        setFormData({
            user_name: '',
            user_phone_code: '+91',
            user_phone_number: '',
            user_email: '',
            password: '',
            password_confirmation: ''
        });
        setErrors({
            user_name: '',
            user_phone_code: '',
            user_phone_number: '',
            user_email: '',
            password: '',
            password_confirmation: ''
        });
    };

    const fetchMembersData = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/team-members`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setMembersList(response?.data?.data?.team_members);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };
    const deleteMemberPopop = (id) => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You want to delete this team member?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, keep it',
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteMember(id);
            }
        });
    }
    const handleDeleteMember = async (id) => {
        try {
            setIsLoading(true);
            const response = await api.delete(`/${userRole}/team-member/${id}`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                fetchMembersData();
                resetForm();
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'Varosa - Clinic Team Members';
        if (!isAuthenticated || !user) {
            navigate('/login');
            return;
        }
        fetchMembersData();
    }, [isAuthenticated, user, navigate]);

    const validateForm = () => {
        let all_good = true;
        let error = {
            user_name: '',
            user_phone_code: '',
            user_phone_number: '',
            user_email: '',
            password: '',
            password_confirmation: ''
        };
        if(formData.user_name.trim() === ''){
            error.user_name = 'Please enter your user name.';
            all_good = false;
        }
        if(formData.user_phone_code.trim() === ''){
            error.user_phone_code = 'Please select a valid country code.';
            all_good = false;
        }
        if(formData.user_phone_number.trim() === ''){
            error.user_phone_number = 'Please enter your phone number.';
            all_good = false;
        }
        if(formData.user_email.trim() === ''){
            error.user_email = 'Please enter your email.';
            all_good = false;
        }
        if(formData.user_email.trim() !== '' && !/^\S+@\S+\.\S+$/.test(formData.user_email.trim())){
            error.user_email = 'Please enter a valid email.';
            all_good = false;
        }
        if(formData.password.trim() === ''){
            error.password = 'Please enter a password.';
            all_good = false;
        }else if(formData.password.length < 8){
            error.password = 'Password must be at least 8 characters.';
            all_good = false;
        }
        if(formData.password_confirmation.trim() === ''){
            error.password_confirmation = 'Please confirm the password.';
            all_good = false;
        }else if(formData.password !== formData.password_confirmation){
            error.password_confirmation = 'Password and confirm password do not match.';
            all_good = false;
        }
        setErrors(error);
        return all_good;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if(!validateForm()){
                errorDivRef.current.scrollIntoView({ behavior: 'smooth' });
                return; 
            }
            const postData = new FormData();
            postData.append('user_name', formData.user_name || '');
            postData.append('user_phone_code', formData.user_phone_code || '');
            postData.append('user_phone_number', formData.user_phone_number || '');
            postData.append('user_email', formData.user_email || '');
            postData.append('password', formData.password || '');
            postData.append('password_confirmation', formData.password_confirmation || '');
            if (formData.profile_pic) {
                postData.append('profile_picture', formData.profile_pic);
            }

            setBtnDisabled(true);
            setIsLoading(true);
            const response = await api.post('/'+userRole+'/team-member/add', postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                fetchMembersData();
                resetForm();
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error updating profile');
        } finally {
            setBtnDisabled(false);
            setIsLoading(false);
        }
    };

    return (
        <>
        <div className="container py-5 login-container">
            <div className="row g-3">
                <div className="col-9">
                    <h2 className="mb-4">Clinic's Team Members</h2>
                </div>
                <div className="col-3 text-end">
                    <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                </div>
            </div>
            {membersList.length <= 5 &&
            <form onSubmit={handleSubmit} className="needs-validation" ref={errorDivRef}>
                <div className="row g-3">
                    <div className="col-md-12 pt-5">
                        <h4>Add new team member</h4>
                        <hr />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Name of User</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="user_name"
                            value={formData.user_name}
                            onChange={handleInputChange}
                        />
                        {errors.user_name && <div className="invalid-feedback d-block">Please enter a valid name of user.</div>}
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">User Phone</label>
                        <div className="input-group mb-3">
                            <select 
                                className="form-select py-3 border-primary"
                                name="user_phone_code"
                                value={formData.user_phone_code}
                                onChange={handleInputChange}
                                style={{ maxWidth: '120px' }}
                            >
                                <option>Choose...</option>
                                {countryCodes.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country.code}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="tel"
                                className="form-control py-3 border-primary"
                                name="user_phone_number"
                                value={formData.user_phone_number}
                                onChange={handleInputChange}
                                pattern="[0-9]{10}"
                            />
                        </div>
                        {errors.user_phone_code && <div className="invalid-feedback d-block">{errors.user_phone_code}</div>}
                        {errors.user_phone_number && <div className="invalid-feedback d-block">{errors.user_phone_number}</div>}
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">User Email ID</label>
                        <input
                            type="email"
                            className="form-control py-3 border-primary"
                            name="user_email"
                            value={formData.user_email}
                            onChange={handleInputChange}
                        />
                        {errors.user_email && <div className="invalid-feedback d-block">Please enter a valid email.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Password</label>
                        <input
                            type="password"
                            className="form-control py-3 border-primary"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            autoComplete="new-password"
                            minLength="8"
                        />
                        {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Confirm Password</label>
                        <input
                            type="password"
                            className="form-control py-3 border-primary"
                            name="password_confirmation"
                            value={formData.password_confirmation}
                            onChange={handleInputChange}
                            autoComplete="new-password"
                            minLength="8"
                        />
                        {errors.password_confirmation && <div className="invalid-feedback d-block">{errors.password_confirmation}</div>}
                    </div>
                </div>
                <div className="mt-4">
                    <button type="submit" className="btn btn-primary" disabled={btnDisabled}>Add Member</button>
                </div>
            </form>}
        </div>
        <div className="container-fluid service bg-light py-5">
            <div className="container py-2">
                <div className="row g-4 mt-2 justify-content-center">
                    {membersList.map(member => 
                    (<div key={member.id} className="col-md-4 col-sm-6 col-xs-12 wow fadeInUp" data-wow-delay="0.1s">
                        <div className="team-item rounded">
                            <div className="team-img rounded-top h-100 border border-primary border-bottom-0 p-2">
                                <img src={getPublicImgUrl()+'/profile_8747415.png'} className="img-fluid rounded-top w-100" style={{ maxHeight: '250px', objectFit: 'contain' }} alt="" />
                            </div>
                            <div className="team-content text-center border border-primary border-top-0 rounded-bottom p-4">
                                <h5 className="mb-4">{member.name}</h5>
                                <p className="mb-0" style={{width: '100%', wordWrap: 'break-word'}}>{member.phone_code+' '+member.phone_number+' ('+member.email+')'}</p>
                                <hr />
                                {member.is_admin ? <button type="button" className="btn btn-info text-white" disabled={true}>Admin User</button> :
                                <button onClick={(e) => { 
                                    e.preventDefault();
                                    deleteMemberPopop(member.id);
                                }} className="btn btn-primary text-white">Delete</button>}
                            </div>
                        </div>
                    </div>))}
                </div>
            </div>
        </div>
        </>
    );
}

export default ClinicTeamMembers;
